/** ⋯ → Add role / affiliation — record by hand that this person holds a role at an organization.
 *
 * Exists because the CRM infers employment from the domain someone mails FROM, which can never see a
 * board seat: a non-executive director writes from his own address, so nothing in the mail corpus links
 * him to the company he chairs. Bill Shannon → Ashtead Technology is the case that motivated it.
 *
 * The company picker is the SAME directory typeahead the ref-cell editor uses (`useCrmLookup`, POST
 * /crm/lookup) — deliberately reused rather than re-implemented, so this modal cannot start offering a
 * different set of companies from the rest of the CRM. It is not the RefCellEditor itself: that one is
 * an ag-grid cell editor (`CustomCellEditorProps`) and is not mountable outside a grid.
 *
 * Save is gated on a PICKED organization — never on typed text. The server re-validates the id anyway,
 * but a free-text company name would silently create the mismatch the picker exists to prevent.
 */
import { useEffect, useState } from 'react';
import styles from './crm.module.css';
import { useCrmLookup } from '../../hooks/useCrm';
import type { CrmLookupResult } from '../../types';

interface Props {
  /** The person the role belongs to — named in the modal so a mis-click is visible before saving. */
  personName: string;
  onSave: (body: { org_id: number; title: string; role?: string }) => void;
  onClose: () => void;
  saving?: boolean;
  /** shown INSIDE the modal — a page-flow banner renders behind the backdrop (CrmRenameModal's lesson). */
  error?: string | null;
}

export function CrmAddRoleModal({ personName, onSave, onClose, saving, error }: Props) {
  const [org, setOrg] = useState<CrmLookupResult | null>(null);
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState('');
  useEffect(() => { const t = setTimeout(() => setDq(q), 250); return () => clearTimeout(t); }, [q]);
  const lookup = useCrmLookup('organization', dq);
  const results = lookup.data?.results ?? [];

  const trimmedTitle = title.trim();
  const canSave = org != null && !!trimmedTitle && !saving;
  const submit = () => {
    if (!canSave || org == null) return;
    onSave({ org_id: org.id, title: trimmedTitle, role: role.trim() || undefined });
  };

  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div className={styles.modalCard} role="dialog" aria-label="Add role"
           onMouseDown={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Add role for {personName}</h3>

        <div className={styles.addRoleField}>
          <span className={styles.fieldLabel}>Organization</span>
          {org != null ? (
            <span className={styles.addRoleChosen}>
              <span className={styles.gridRef}>{org.name || `#${org.id}`}</span>
              <button type="button" className={styles.gridMiniBtn}
                      onClick={() => { setOrg(null); setQ(''); }}>Change</button>
            </span>
          ) : (
            <>
              <input className={styles.refEditorSearch} autoFocus value={q}
                     placeholder="Search organizations…" aria-label="Search organizations"
                     onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }} />
              <div className={styles.refEditorResults}>
                {q.trim().length < 2 && <span className={styles.gridEmpty}>Type at least 2 characters.</span>}
                {/* "No matches" only once the debounce has SETTLED on the current input — otherwise it
                    flashes for ~250ms on every keystroke, which reads as "this company isn't here". */}
                {q.trim().length >= 2 && dq === q && results.length === 0 && !lookup.isFetching && (
                  <span className={styles.gridEmpty}>No matches.</span>
                )}
                {results.map((r) => (
                  <button key={r.id} type="button" className={styles.refEditorResult}
                          onClick={() => setOrg(r)}>
                    {r.name || `#${r.id}`}
                    {r.detail ? <span className={styles.refEditorDetail}> · {r.detail}</span> : null}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className={styles.addRoleField}>
          <span className={styles.fieldLabel}>Title</span>
          <input className={styles.modalInput} maxLength={200} value={title}
                 aria-label="Role title" placeholder="e.g. Independent Chairman"
                 onChange={(e) => setTitle(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onClose(); }} />
        </div>

        <div className={styles.addRoleField}>
          <span className={styles.fieldLabel}>Role (optional)</span>
          <input className={styles.modalInput} maxLength={60} value={role}
                 aria-label="Role kind" placeholder="e.g. board"
                 onChange={(e) => setRole(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onClose(); }} />
        </div>

        {error && <div className={styles.modalError} role="alert">{error}</div>}
        <div className={styles.modalActions}>
          <button type="button" className={styles.gridMiniBtn} onClick={onClose}>Cancel</button>
          <button type="button" className={styles.gridPageBtn} disabled={!canSave} onClick={submit}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
